import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../supabaseClient";

const categoryOptions = [
  "All",
  "Outdoor",
  "Arts",
  "Recreation",
  "Education",
  "Wellness",
  "Sports",
];

export default function Activities() {
  const [activities, setActivities] = useState([]);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  useEffect(() => {
    fetchActivities();
  }, []);

  async function fetchActivities() {
    setLoading(true);
    setMessage("");

    const { data, error } = await supabase
      .from("activities")
      .select("*")
      .eq("is_active", true)
      .eq("approved", true)
      .order("name", { ascending: true });

    if (error) {
      console.error("Error loading activities:", error);
      setActivities([]);
      setMessage("There was an error loading activities.");
    } else {
      setActivities(data || []);
    }

    setLoading(false);
  }

  const filteredActivities = useMemo(() => {
    const q = search.toLowerCase().trim();

    return activities.filter((activity) => {
      const matchesSearch =
        q === "" ||
        (activity.name || "").toLowerCase().includes(q) ||
        (activity.location || "").toLowerCase().includes(q) ||
        (activity.address || "").toLowerCase().includes(q) ||
        (activity.description || "").toLowerCase().includes(q) ||
        (activity.category || "").toLowerCase().includes(q) ||
        (activity.age_group || "").toLowerCase().includes(q) ||
        (activity.cost || "").toLowerCase().includes(q) ||
        (activity.schedule || "").toLowerCase().includes(q) ||
        (activity.accessibility || "").toLowerCase().includes(q) ||
        (activity.contact_name || "").toLowerCase().includes(q) ||
        (activity.contact_email || "").toLowerCase().includes(q) ||
        (activity.contact_phone || "").toLowerCase().includes(q);

      const matchesCategory =
        categoryFilter === "All" || activity.category === categoryFilter;

      return matchesSearch && matchesCategory;
    });
  }, [activities, search, categoryFilter]);

  return (
    <section className="activities-page">
      <div className="activities-card">
        <h1>Activities Near You</h1>

        <p className="activities-intro">
          Explore local activity ideas, community programs, and recreational
          opportunities for individuals and families.
        </p>

        <div className="activities-controls">
          <input
            type="text"
            placeholder="Search by activity, county, age group, keyword, or contact..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="activities-search"
          />

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="activities-select"
          >
            {categoryOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="no-results">Loading activities...</div>
        ) : message ? (
          <div className="no-results">{message}</div>
        ) : (
          <div className="activities-grid">
            <Link to="/add-activity" className="activity-box add-activity-box">
              <div className="activity-category">Admin</div>

              <h2>Add Activity</h2>

              <div className="activity-location">Open activity form</div>

              <p>
                Add a new community activity, program, or event to the list.
                This box will always stay first in the grid.
              </p>

              <div className="activity-links">
                <div className="activity-button">Add Activity</div>
              </div>
            </Link>

            {filteredActivities.length > 0 ? (
              filteredActivities.map((activity) => (
                <div key={activity.id} className="activity-box">
                  <div className="activity-category">{activity.category}</div>

                  <h2>{activity.name}</h2>

                  <div className="activity-location">{activity.location}</div>

                  <div className="activity-meta">
                    {activity.address && (
                      <div>
                        <strong>Address:</strong> {activity.address}
                      </div>
                    )}

                    {activity.age_group && (
                      <div>
                        <strong>Age Group:</strong> {activity.age_group}
                      </div>
                    )}

                    {activity.cost && (
                      <div>
                        <strong>Cost:</strong> {activity.cost}
                      </div>
                    )}

                    {activity.schedule && (
                      <div>
                        <strong>Schedule:</strong> {activity.schedule}
                      </div>
                    )}

                    {activity.accessibility && (
                      <div>
                        <strong>Accessibility:</strong> {activity.accessibility}
                      </div>
                    )}
                  </div>

                  {activity.description && <p>{activity.description}</p>}

                  {(activity.contact_name ||
                    activity.contact_email ||
                    activity.contact_phone) && (
                    <div className="activity-contact">
                      <h3>Contact Information</h3>

                      {activity.contact_name && (
                        <div>
                          <strong>Contact:</strong> {activity.contact_name}
                        </div>
                      )}

                      {activity.contact_email && (
                        <div>
                          <strong>Email:</strong> {activity.contact_email}
                        </div>
                      )}

                      {activity.contact_phone && (
                        <div>
                          <strong>Phone:</strong> {activity.contact_phone}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="activity-links">
                    {activity.website && (
                      <a
                        href={activity.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="activity-button"
                      >
                        View Details
                      </a>
                    )}

                    {activity.maps_link && (
                      <a
                        href={activity.maps_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="activity-button secondary"
                      >
                        View Map
                      </a>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="no-results">
                No activities found matching your search.
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`
        .activities-page {
          padding: 20px 0;
        }

        .activities-card {
          background: white;
          border-radius: 20px;
          padding: 40px;
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.08);
        }

        .activities-card h1 {
          margin-top: 0;
          text-align: center;
          margin-bottom: 12px;
        }

        .activities-intro {
          text-align: center;
          max-width: 800px;
          margin: 0 auto 30px auto;
          color: #6b7280;
          font-size: 1.05rem;
          line-height: 1.7;
        }

        .activities-controls {
          display: flex;
          gap: 16px;
          margin-bottom: 30px;
          flex-wrap: wrap;
        }

        .activities-search,
        .activities-select {
          padding: 14px;
          border: 1px solid #dbe1ea;
          border-radius: 12px;
          font-size: 1rem;
          box-sizing: border-box;
        }

        .activities-search {
          flex: 1;
          min-width: 260px;
        }

        .activities-select {
          width: 220px;
          background: white;
        }

        .activities-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
        }

        .activity-box {
          background: #f9fbfd;
          border: 1px solid #dbe1ea;
          border-radius: 16px;
          padding: 22px;
        }

        .activity-category {
          display: inline-block;
          background: #2f6f89;
          color: white;
          font-size: 0.85rem;
          font-weight: 600;
          padding: 6px 10px;
          border-radius: 999px;
          margin-bottom: 14px;
        }

        .activity-box h2 {
          margin: 0 0 8px 0;
          font-size: 1.2rem;
          color: #1f2937;
        }

        .activity-location {
          font-weight: 600;
          color: #2f6f89;
          margin-bottom: 14px;
        }

        .activity-meta {
          margin-bottom: 14px;
          display: grid;
          gap: 8px;
          color: #374151;
          font-size: 0.96rem;
        }

        .activity-box p {
          margin: 0 0 18px 0;
          color: #4b5563;
          line-height: 1.7;
        }

        .activity-contact {
          margin: 18px 0;
          padding: 14px;
          background: #ffffff;
          border: 1px solid #dbe1ea;
          border-radius: 12px;
          display: grid;
          gap: 8px;
          color: #374151;
          font-size: 0.96rem;
        }

        .activity-contact h3 {
          margin: 0 0 4px 0;
          font-size: 1rem;
          color: #2f6f89;
        }

        .activity-links {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .activity-button {
          display: inline-block;
          text-decoration: none;
          background: #2f6f89;
          color: white;
          padding: 10px 16px;
          border-radius: 10px;
          font-weight: 600;
          border: none;
          cursor: pointer;
        }

        .activity-button:hover {
          opacity: 0.92;
        }

        .activity-button.secondary {
          background: #e8eef3;
          color: #1f2937;
        }

        .no-results {
          text-align: center;
          padding: 30px;
          border: 1px dashed #cbd5e1;
          border-radius: 16px;
          color: #6b7280;
          background: #f8fafc;
          grid-column: 1 / -1;
        }

        .add-activity-box {
          cursor: pointer;
          border: 2px dashed #2f6f89;
          background: #f4f9fc;
          text-decoration: none;
          color: inherit;
        }

        .add-activity-box:hover {
          background: #eaf4f8;
        }

        @media (max-width: 900px) {
          .activities-grid {
            grid-template-columns: 1fr;
          }

          .activities-card {
            padding: 24px;
          }

          .activities-select {
            width: 100%;
          }
        }
      `}</style>
    </section>
  );
}